import { useState, useEffect, useRef } from "react";
import { useI18n } from "./i18n";
import SiteNav from "./SiteNav";
import WebRRunner from "./WebRRunner";
import { generateReport } from "./generateReport";

// ═══ DESIGN TOKENS ═══
const CRIMSON = "#C0392B";
const DARK = "#1D2B3A";
const LIGHT_BG = "#F8F7F4";
const CARD_BG = "#FFFFFF";
const MUTED = "#6B7A8D";
const LIGHT_BORDER = "#E8E6E1";
const GREEN = "#3DA87A";
const RED = "#D94B2E";
const AMBER = "#D4A843";
const BLUE = "#2E86C1";
const FONT = "'Noto Sans TC', 'Outfit', sans-serif";

// ═══ BILINGUAL TEXT ═══
const T = {
  zh: {
    title: "研究工作坊：分析篇",
    subtitle: "選擇分析方法、執行統合分析、解讀結果、撰寫結論",
    step1: "效果量與模型",
    step2: "準備資料",
    step3: "執行分析",
    step4: "解讀與報告",
    step5: "撰寫結論",
    next: "下一步",
    prev: "上一步",

    // Step 1: Effect size & model
    effectLabel: "效果量類型",
    effectOR: "勝算比 (OR)",
    effectRR: "相對風險 (RR)",
    effectRD: "風險差 (RD)",
    effectMD: "均值差 (MD)",
    effectSMD: "標準化均值差 (SMD)",
    modelLabel: "統合分析模型",
    modelFixed: "固定效果模型 (Fixed-Effect)",
    modelRandom: "隨機效果模型 (Random-Effects)",
    modelFixedDesc: "假設所有研究估計同一個真實效果。適合同質性高的研究。",
    modelRandomDesc: "假設各研究有不同的真實效果。較常用，尤其當研究間有差異時。",
    rationale: "為什麼選擇這個效果量和模型？",
    rationalePh: "例如：結局為二元變項（事件發生與否），且各研究族群和劑量不同，因此選擇 OR + 隨機效果模型。",
    binaryNote: "你的資料為二元結局 — 建議使用 OR 或 RR。",
    contNote: "你的資料為連續結局 — 建議使用 MD 或 SMD。",
    effectWhy: {
      OR: "OR 適用於病例對照研究和罕見事件。大多數臨床 MA 使用 OR。",
      RR: "RR 較直觀，適用於前瞻性研究。不適合病例對照設計。",
      RD: "RD 直接顯示絕對風險差異，可計算 NNT。但各研究基線風險差異大時不穩定。",
      MD: "MD 適用於所有研究使用相同量表測量結局時。",
      SMD: "SMD (Hedges' g) 適用於不同量表但測量相同構念時。",
    },

    // Step 2: Data prep
    prepTitle: "準備分析資料",
    prepDesc: "下方表格整理了你在規劃篇萃取的資料。你可以：",
    prepOption1: "複製表格到線上計算器",
    prepOption2: "下載 CSV 檔案",
    prepOption3: "複製 R 程式碼",
    copyTable: "複製表格",
    downloadCSV: "下載 CSV",
    copied: "已複製！",
    studyLabel: "研究",
    eventsI: "介入組事件數",
    totalI: "介入組總人數",
    eventsC: "對照組事件數",
    totalC: "對照組總人數",
    meanI: "介入組平均值",
    sdI: "介入組 SD",
    nI: "介入組人數",
    meanC: "對照組平均值",
    sdC: "對照組 SD",
    nC: "對照組人數",

    // Step 3: Run analysis
    runTitle: "執行你的統合分析",
    runDesc: "選擇一種方式執行分析，取得森林圖和統計結果：",
    tabOnline: "線上計算器（免安裝）",
    tabR: "R 程式碼（metafor）",
    tabWebR: "瀏覽器內分析（推薦）",
    onlineTitle: "使用 Onlinemeta 線上計算器",
    onlineSteps: [
      "點擊下方連結開啟 Onlinemeta",
      "在左側選單選擇「Meta」",
      "選擇資料類型（Dichotomous 或 Continuous）",
      "將上一步複製的資料貼入，或上傳 CSV",
      "選擇效果量類型和模型（Fixed/Random）",
      "點擊「Run」產生森林圖和漏斗圖",
      "下載圖片（PDF 或 PNG）用於你的海報",
    ],
    onlineLink: "開啟 Onlinemeta →",
    onlineAlt: "其他線上工具：",
    rTitle: "使用 R + metafor 套件",
    rSteps: [
      "確認已安裝 R 和 RStudio",
      "複製下方程式碼到 RStudio",
      "執行程式碼（Ctrl+A 全選，然後 Ctrl+Enter）",
      "森林圖和漏斗圖會自動產生",
      "使用 RStudio 匯出圖片",
    ],
    rCopyCode: "複製 R 程式碼",
    rInstallNote: "若尚未安裝 metafor，先執行：install.packages(\"metafor\")",

    // Step 4: Interpret & Report
    interpretTitle: "解讀結果與報告",
    forestQ1: "整體效果量代表什麼意義？是否具統計顯著性？",
    forestQ1Ph: "例如：OR = 0.67 表示介入組發生事件的勝算為對照組的 67%（勝算降低 33%），95% CI 不跨越 1，具統計顯著性。",
    interpretNowTitle: "✍️ 解讀你的結果",
    interpretNowDesc: "看完分析結果後，試著用自己的話回答以下問題：",
    reviewTitle: "檢查你的解讀",
    reviewDesc: "以下是你在上一步填寫的解讀。確認無誤後，可請 AI 檢查。",
    reviewEmpty: "（尚未填寫）",
    reviewEdit: "返回上一步修改",
    forestQ2: "各研究的效果方向是否一致？哪篇權重最大？",
    forestQ2Ph: "例如：5 篇研究效果方向一致，均偏向介入組。EMPA-KIDNEY 權重最大，因樣本數最多且信賴區間最窄。",
    hetInterpret: "你如何解讀異質性？",
    hetInterpretPh: "例如：I²=35% 表示低至中度異質性，研究間差異可能來自族群定義不同（CKD 專屬 vs 高 CV 風險）。",
    funnelInterpret: "你如何解讀漏斗圖？（若有產生）",
    funnelPh: "例如：圖形大致對稱，未見明顯發表偏差。小型研究稍偏右側，但研究數不足以確認。",
    advInterpretLabel: {
      leaveOneOut: "你如何解讀逐一排除分析結果？",
      trimFill: "你如何解讀 Trim-and-Fill 分析結果？",
      eggers: "你如何解讀 Egger's 檢定結果？",
      influence: "你如何解讀影響力診斷結果？",
      subgroup: "你如何解讀次群組分析結果？",
      metareg: "你如何解讀統合迴歸結果？",
    },
    advInterpretPh: {
      leaveOneOut: "例如：排除任一研究後結果穩定，最大變動僅 0.017，結論穩健。",
      trimFill: "例如：估計缺失 0 篇研究，無明顯發表偏差。",
      eggers: "例如：p = 0.45，漏斗圖無顯著不對稱。但研究數僅 8 篇，檢定效力有限。",
      influence: "例如：沒有研究的 Cook's D 異常偏高，結果不受個別研究主導。",
      subgroup: "例如：兩組效果方向一致，但 QM 檢定不顯著（p = 0.32），組間無顯著差異。",
      metareg: "例如：調節變項不顯著（p = 0.15），R² = 0%，無法解釋異質性。",
    },
    aiCheckInterpret: "AI 檢查解讀",
    aiCheckingInterpret: "AI 檢查中...",

    // Step 6: Conclusions
    conclusionTitle: "撰寫結論",
    mainFinding: "主要發現",
    mainFindingPh: "用 1-2 句話總結你的統合分析結果。",
    certainty: "證據確定性 (GRADE)",
    gradeHigh: "高",
    gradeMod: "中等",
    gradeLow: "低",
    gradeVLow: "極低",
    certRationale: "GRADE 評級理由",
    certRationalePh: "例如：因研究間異質性降一級，因部分研究對照組事件率差異降一級。",
    limitations: "主要限制",
    limitationsPh: "例如：納入研究以大型跨國試驗為主，可能對特定族群的推論受限。",
    implications: "臨床意義",
    implicationsPh: "例如：SGLT2 抑制劑對 CKD 合併 T2DM 患者的腎臟保護效果明確，應納入臨床指引考量。",

    // Completion
    congrats: "🎉 恭喜完成！",
    congratsDesc: "你已完成一個完整的統合分析流程——從定義 PICO 到撰寫結論。將你的分析結果和圖表加入學術海報吧！",
    summaryTitle: "你的統合分析摘要",
    aiFullReview: "AI 全面審查",
    aiFullReviewing: "AI 審查中...",
    aiFullReviewDesc: "AI 會檢視你的整個專案（從 PICO 到結論），找出步驟間的不一致、遺漏、和可改進之處。",
    backToHub: "返回課程主頁",
    noData: "請先完成規劃篇（Phase 1）",
    goToMidterm: "前往規劃篇 →",
  },
  en: {
    title: "MA Workshop: Analysis",
    subtitle: "Choose analysis methods, run your meta-analysis, interpret results, write conclusions",
    step1: "Effect Size & Model",
    step2: "Prepare Data",
    step3: "Run Analysis",
    step4: "Interpret & Report",
    step5: "Conclusions",
    next: "Next",
    prev: "Back",

    effectLabel: "Effect Size Type",
    effectOR: "Odds Ratio (OR)",
    effectRR: "Risk Ratio (RR)",
    effectRD: "Risk Difference (RD)",
    effectMD: "Mean Difference (MD)",
    effectSMD: "Standardized Mean Difference (SMD)",
    modelLabel: "Meta-Analysis Model",
    modelFixed: "Fixed-Effect Model",
    modelRandom: "Random-Effects Model",
    modelFixedDesc: "Assumes all studies estimate the same true effect. Best when studies are homogeneous.",
    modelRandomDesc: "Assumes each study has a different true effect. More common, especially with clinical diversity.",
    rationale: "Why did you choose this effect size and model?",
    rationalePh: "e.g., Outcome is binary (event occurrence), and studies vary in population and dose, so we chose OR + random-effects.",
    binaryNote: "Your data is binary — OR or RR are recommended.",
    contNote: "Your data is continuous — MD or SMD are recommended.",
    effectWhy: {
      OR: "OR works well for case-control studies and rare events. Most clinical MAs use OR.",
      RR: "RR is more intuitive for prospective studies. Not suitable for case-control designs.",
      RD: "RD shows absolute risk difference and allows NNT calculation. Unstable when baseline risks vary.",
      MD: "MD is appropriate when all studies measure the outcome on the same scale.",
      SMD: "SMD (Hedges' g) is appropriate when studies use different scales measuring the same construct.",
    },

    prepTitle: "Prepare Your Data",
    prepDesc: "The table below organizes the data you extracted in the Planning phase. You can:",
    prepOption1: "Copy the table into an online calculator",
    prepOption2: "Download as CSV file",
    prepOption3: "Copy a ready-made R script",
    copyTable: "Copy Table",
    downloadCSV: "Download CSV",
    copied: "Copied!",
    studyLabel: "Study",
    eventsI: "Intervention Events",
    totalI: "Intervention Total",
    eventsC: "Control Events",
    totalC: "Control Total",
    meanI: "Intervention Mean",
    sdI: "Intervention SD",
    nI: "Intervention N",
    meanC: "Control Mean",
    sdC: "Control SD",
    nC: "Control N",

    runTitle: "Run Your Meta-Analysis",
    runDesc: "Choose one method to run the analysis and get your forest plot and statistics:",
    tabOnline: "Online Calculator (no install)",
    tabR: "R Code (metafor)",
    tabWebR: "In-Browser Analysis (recommended)",
    onlineTitle: "Using the Onlinemeta Calculator",
    onlineSteps: [
      "Click the link below to open Onlinemeta",
      "Select \"Meta\" from the left sidebar",
      "Choose your data type (Dichotomous or Continuous)",
      "Paste your copied data or upload the CSV from the previous step",
      "Select your effect size type and model (Fixed/Random)",
      "Click \"Run\" to generate forest plot and funnel plot",
      "Download the plots (PDF or PNG) for your poster",
    ],
    onlineLink: "Open Onlinemeta →",
    onlineAlt: "Alternative online tools:",
    rTitle: "Using R + metafor Package",
    rSteps: [
      "Make sure R and RStudio are installed",
      "Copy the code below into RStudio",
      "Run the code (Ctrl+A to select all, then Ctrl+Enter)",
      "Forest plot and funnel plot will be generated automatically",
      "Export plots using RStudio's plot export feature",
    ],
    rCopyCode: "Copy R Code",
    rInstallNote: "If metafor is not installed, run first: install.packages(\"metafor\")",

    // Step 4: Interpret & Report
    interpretTitle: "Interpret & Report Results",
    forestQ1: "What does the pooled effect mean? Is it statistically significant?",
    forestQ1Ph: "e.g., OR = 0.67 means the intervention group had 33% lower odds of the event, and the 95% CI does not cross 1, so it's statistically significant.",
    interpretNowTitle: "✍️ Interpret your results",
    interpretNowDesc: "After reviewing the output above, answer these questions in your own words:",
    reviewTitle: "Review your interpretation",
    reviewDesc: "Below is what you wrote in the previous step. Confirm or go back to edit, then ask AI to check.",
    reviewEmpty: "(not yet filled)",
    reviewEdit: "Go back to edit",
    forestQ2: "Are effect directions consistent across studies? Which study has the highest weight?",
    forestQ2Ph: "e.g., All 5 studies favor the intervention. EMPA-KIDNEY has the highest weight due to the largest sample size and narrowest CI.",
    hetInterpret: "How do you interpret the heterogeneity?",
    hetInterpretPh: "e.g., I²=35% suggests low-to-moderate heterogeneity, possibly due to differences in population definitions (CKD-specific vs high CV risk).",
    funnelInterpret: "How do you interpret the funnel plot? (if generated)",
    funnelPh: "e.g., The plot appears roughly symmetric with no obvious publication bias. Smaller studies cluster slightly to one side, but the number of studies is too small to confirm.",
    advInterpretLabel: {
      leaveOneOut: "How do you interpret the leave-one-out results?",
      trimFill: "How do you interpret the trim-and-fill results?",
      eggers: "How do you interpret Egger's test results?",
      influence: "How do you interpret the influence diagnostics?",
      subgroup: "How do you interpret the subgroup analysis?",
      metareg: "How do you interpret the meta-regression results?",
    },
    advInterpretPh: {
      leaveOneOut: "e.g., Results stable after removing any study; max change only 0.017. Conclusion is robust.",
      trimFill: "e.g., 0 missing studies estimated; no evidence of publication bias.",
      eggers: "e.g., p = 0.45, no significant funnel asymmetry. But with only 8 studies, power is limited.",
      influence: "e.g., No study has unusually high Cook's D; results not driven by any single study.",
      subgroup: "e.g., Both groups show consistent effects; QM test non-significant (p = 0.32), no between-group difference.",
      metareg: "e.g., Moderator not significant (p = 0.15), R² = 0%, does not explain heterogeneity.",
    },
    aiCheckInterpret: "AI Review Interpretation",
    aiCheckingInterpret: "AI reviewing...",

    conclusionTitle: "Write Conclusions",
    mainFinding: "Main Finding",
    mainFindingPh: "Summarize your meta-analysis result in 1-2 sentences.",
    certainty: "Certainty of Evidence (GRADE)",
    gradeHigh: "High",
    gradeMod: "Moderate",
    gradeLow: "Low",
    gradeVLow: "Very Low",
    certRationale: "Rationale for GRADE rating",
    certRationalePh: "e.g., Downgraded one level for heterogeneity, one level for indirectness in some trials.",
    limitations: "Key Limitations",
    limitationsPh: "e.g., Included studies are predominantly large multinational trials, limiting generalizability to specific populations.",
    implications: "Clinical Implications",
    implicationsPh: "e.g., SGLT2 inhibitors show clear renoprotective benefit in T2DM+CKD patients and should be considered in clinical guidelines.",

    congrats: "🎉 Congratulations!",
    congratsDesc: "You've completed a full meta-analysis workflow — from defining PICO to writing conclusions. Add your results and plots to your academic poster!",
    summaryTitle: "Your Meta-Analysis Summary",
    aiFullReview: "AI Full Review",
    aiFullReviewing: "AI reviewing...",
    aiFullReviewDesc: "AI will review your entire project (PICO through conclusions), checking for cross-step inconsistencies, omissions, and areas for improvement.",
    backToHub: "Back to Course Hub",
    noData: "Please complete the Planning phase (Phase 1) first",
    goToMidterm: "Go to Planning Workshop →",
  },
};

// ═══ REUSABLE UI ═══
function InputField({ label, value, onChange, placeholder, multiline, style = {} }) {
  const shared = { width: "100%", padding: "10px 14px", borderRadius: 10, border: `1px solid ${LIGHT_BORDER}`, fontSize: 14, fontFamily: FONT, color: DARK, background: CARD_BG, outline: "none", transition: "border 0.2s", boxSizing: "border-box", ...style };
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: DARK, marginBottom: 6 }}>{label}</label>}
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} style={{ ...shared, resize: "vertical" }} onFocus={e => e.target.style.borderColor = CRIMSON} onBlur={e => e.target.style.borderColor = LIGHT_BORDER} />
        : <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={shared} onFocus={e => e.target.style.borderColor = CRIMSON} onBlur={e => e.target.style.borderColor = LIGHT_BORDER} />}
    </div>
  );
}

function Btn({ children, onClick, primary, disabled, small, style = {} }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} disabled={disabled} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ padding: small ? "6px 16px" : "10px 24px", borderRadius: 10, fontSize: small ? 12 : 14, fontWeight: 600, fontFamily: FONT, cursor: disabled ? "not-allowed" : "pointer", border: primary ? "none" : `1.5px solid ${LIGHT_BORDER}`, background: disabled ? "#DDD" : primary ? (hov ? "#A0312A" : CRIMSON) : (hov ? "#F5F4F0" : CARD_BG), color: primary ? "#FFF" : DARK, transition: "all 0.2s", opacity: disabled ? 0.5 : 1, ...style }}>
      {children}
    </button>
  );
}

function Card({ children, style = {} }) {
  return <div style={{ background: CARD_BG, borderRadius: 16, border: `1px solid ${LIGHT_BORDER}`, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.03)", ...style }}>{children}</div>;
}

function Hint({ children }) {
  return <div style={{ background: `${CRIMSON}08`, border: `1px solid ${CRIMSON}22`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: MUTED, lineHeight: 1.6, marginBottom: 16 }}>💡 {children}</div>;
}

function AiFeedbackBox({ feedback, loading, lang }) {
  if (loading) return <div style={{ background: `${CRIMSON}08`, borderRadius: 12, padding: 16, marginTop: 12, textAlign: "center" }}><div style={{ fontSize: 13, color: CRIMSON }}>{lang === "zh" ? "AI 分析中..." : "AI analyzing..."} <span style={{ display: "inline-block", animation: "pulse 1.2s infinite" }}>⏳</span></div></div>;
  if (!feedback) return null;
  const isPass = feedback.includes("✅");
  return <div style={{ background: isPass ? `${GREEN}08` : `${AMBER}08`, border: `1px solid ${isPass ? GREEN : AMBER}30`, borderRadius: 12, padding: 16, marginTop: 12 }}><div style={{ fontSize: 13, color: DARK, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{feedback}</div></div>;
}

function StepIndicator({ steps, current, onStepClick }) {
  return (
    <div style={{ display: "flex", gap: 0, marginBottom: 32, overflowX: "auto", padding: "0 0 8px" }}>
      {steps.map((label, i) => {
        const active = i === current, done = i < current;
        const clickable = done || active;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
            <div onClick={() => clickable && onStepClick && onStepClick(i)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 10, background: active ? `${CRIMSON}14` : "transparent", minWidth: 0, cursor: clickable ? "pointer" : "default" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", flexShrink: 0, background: done ? GREEN : active ? CRIMSON : LIGHT_BORDER, color: done || active ? "#FFF" : MUTED, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>{done ? "✓" : i + 1}</div>
              <span style={{ fontSize: 11, fontWeight: active ? 600 : 400, color: active ? DARK : MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
            </div>
            {i < steps.length - 1 && <div style={{ flex: 1, height: 2, background: done ? GREEN : LIGHT_BORDER, minWidth: 8, margin: "0 2px" }} />}
          </div>
        );
      })}
    </div>
  );
}

function CopyButton({ text, label, copiedLabel }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => { navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
  return <Btn small onClick={handleCopy} style={{ background: copied ? `${GREEN}14` : CARD_BG, borderColor: copied ? GREEN : LIGHT_BORDER, color: copied ? GREEN : DARK }}>{copied ? `✓ ${copiedLabel}` : label}</Btn>;
}

// ═══ DATA HELPERS ═══
function getIncluded(project) { return (project?.studies || []).filter(s => s.included); }
function isBinary(studies) { return studies.length > 0 && studies[0].outcomeType === "binary"; }

function buildTSV(studies, bin) {
  if (bin) {
    const h = ["Study", "Events_I", "Total_I", "Events_C", "Total_C"].join("\t");
    return [h, ...studies.map(s => [s.citation, s.tx?.events, s.tx?.total, s.ctrl?.events, s.ctrl?.total].join("\t"))].join("\n");
  }
  const h = ["Study", "Mean_I", "SD_I", "N_I", "Mean_C", "SD_C", "N_C"].join("\t");
  return [h, ...studies.map(s => [s.citation, s.txCont?.mean, s.txCont?.sd, s.txCont?.n, s.ctrlCont?.mean, s.ctrlCont?.sd, s.ctrlCont?.n].join("\t"))].join("\n");
}

function buildCSV(studies, bin) {
  const esc = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
  if (bin) {
    return ["Study,Events_I,Total_I,Events_C,Total_C", ...studies.map(s => [esc(s.citation), s.tx?.events, s.tx?.total, s.ctrl?.events, s.ctrl?.total].join(","))].join("\n");
  }
  return ["Study,Mean_I,SD_I,N_I,Mean_C,SD_C,N_C", ...studies.map(s => [esc(s.citation), s.txCont?.mean, s.txCont?.sd, s.txCont?.n, s.ctrlCont?.mean, s.ctrlCont?.sd, s.ctrlCont?.n].join(","))].join("\n");
}

function buildRCode(studies, bin, effectType, model, modColumns) {
  const method = model === "fixed" ? 'method = "FE"' : 'method = "REML"';
  const measure = effectType || "OR";
  const q = s => `"${s.citation.replace(/"/g, '\\"')}"`;
  // Empty-string and null cells become R's NA. Plain `??` lets `""` through, which
  // produces invalid R like `c(245, , 432)`.
  const num = v => (v === "" || v == null) ? "NA" : v;

  // Build moderator column lines for the data.frame
  const modLines = (modColumns || []).map(col => {
    const rName = col.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
    const vals = studies.map(s => {
      const v = (s.moderators || {})[col] || "";
      // If all values are numeric, don't quote; otherwise quote as character
      return v;
    });
    const allNumeric = vals.every(v => v !== "" && !isNaN(Number(v)));
    if (allNumeric) {
      return `  ${rName} = c(${vals.map(v => v || "NA").join(", ")})`;
    }
    return `  ${rName} = c(${vals.map(v => v ? `"${v.replace(/"/g, '\\"')}"` : "NA").join(", ")})`;
  });
  const modComment = modLines.length > 0 ? `,\n${modLines.join(",\n")}` : "";

  let code = `# ══════════════════════════════════════════════════
# Meta-Analysis with metafor
# Generated by 統合分析 101 / Meta-Analysis 101
# ══════════════════════════════════════════════════

# install.packages("metafor")  # Run once if needed
library(metafor)

`;
  if (bin) {
    code += `# ── Study Data (Binary Outcome) ──
dat <- data.frame(
  study = c(${studies.map(q).join(", ")}),
  ai    = c(${studies.map(s => num(s.tx?.events)).join(", ")}),   # intervention events
  n1i   = c(${studies.map(s => num(s.tx?.total)).join(", ")}),  # intervention total
  ci    = c(${studies.map(s => num(s.ctrl?.events)).join(", ")}),   # control events
  n2i   = c(${studies.map(s => num(s.ctrl?.total)).join(", ")})${modComment}
)

# ── Calculate Effect Sizes ──
dat <- escalc(measure = "${measure}",
              ai = ai, n1i = n1i,
              ci = ci, n2i = n2i,
              data = dat)

`;
  } else {
    code += `# ── Study Data (Continuous Outcome) ──
dat <- data.frame(
  study = c(${studies.map(q).join(", ")}),
  m1i   = c(${studies.map(s => num(s.txCont?.mean)).join(", ")}),   # intervention mean
  sd1i  = c(${studies.map(s => num(s.txCont?.sd)).join(", ")}),  # intervention SD
  n1i   = c(${studies.map(s => num(s.txCont?.n)).join(", ")}),   # intervention N
  m2i   = c(${studies.map(s => num(s.ctrlCont?.mean)).join(", ")}),   # control mean
  sd2i  = c(${studies.map(s => num(s.ctrlCont?.sd)).join(", ")}),  # control SD
  n2i   = c(${studies.map(s => num(s.ctrlCont?.n)).join(", ")})${modComment}
)

# ── Calculate Effect Sizes ──
dat <- escalc(measure = "${measure}",
              m1i = m1i, sd1i = sd1i, n1i = n1i,
              m2i = m2i, sd2i = sd2i, n2i = n2i,
              data = dat)

`;
  }

  const isLog = ["OR", "RR"].includes(measure);
  code += `# ── Run Meta-Analysis ──
res <- rma(yi, vi, data = dat, ${method}, slab = dat$study)

# ── Results ──
cat("══════════════════════════════════════\\n")
cat("  ${measure} Meta-Analysis Results\\n")
cat("══════════════════════════════════════\\n\\n")
cat("Model: ${model === "fixed" ? "Fixed-Effect" : "Random-Effects"} | Studies:", res$k, "\\n\\n")
cat("── Pooled Effect ──\\n")
cat("${measure}:", ${isLog ? "round(exp(coef(res)), 4)" : "round(coef(res), 4)"}, "\\n")
cat("95% CI:", ${isLog ? "round(exp(res$ci.lb), 4), \"to\", round(exp(res$ci.ub), 4)" : "round(res$ci.lb, 4), \"to\", round(res$ci.ub, 4)"}, "\\n")
cat("p-value:", formatC(res$pval, format = "g", digits = 4), "\\n\\n")
cat("── Heterogeneity ──\\n")
cat("I²:", round(res$I2, 1), "%")
if (res$I2 == 0) cat("  (none)\\n") else if (res$I2 <= 25) cat("  (low)\\n") else if (res$I2 <= 50) cat("  (moderate)\\n") else cat("  (high)\\n")
cat("tau²:", round(res$tau2, 4), "\\n")
cat("Q =", round(res$QE, 2), "(df =", res$k - 1, ", p =", round(res$QEp, 4), ")\\n")

# ── Forest Plot ──
forest(res, header = TRUE,
       ${isLog ? 'atransf = exp,\n       ' : ""}xlab = "${measure} (95% CI)",
       cex = 0.9)

# ── Funnel Plot ──
funnel(res, main = "Funnel Plot")
`;
  return code;
}

function downloadFile(content, filename) {
  const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.download = filename; a.href = URL.createObjectURL(blob); a.click();
}

const cellSt = { padding: "8px 10px", textAlign: "center", borderBottom: `1px solid ${LIGHT_BORDER}`, fontFamily: "monospace", fontSize: 13 };

// ═══ STEP COMPONENTS ═══

function Step1({ analysis, setA, project, lang }) {
  const tx = T[lang];
  const bin = isBinary(getIncluded(project));
  const opts = bin
    ? [{ key: "OR", label: tx.effectOR }, { key: "RR", label: tx.effectRR }, { key: "RD", label: tx.effectRD }]
    : [{ key: "MD", label: tx.effectMD }, { key: "SMD", label: tx.effectSMD }];

  return (
    <div>
      <Hint>{bin ? tx.binaryNote : tx.contNote}</Hint>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: DARK, marginBottom: 10 }}>{tx.effectLabel}</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
        {opts.map(o => (
          <div key={o.key} onClick={() => setA(p => ({ ...p, effectType: o.key }))} style={{ padding: "12px 16px", borderRadius: 12, cursor: "pointer", border: `1.5px solid ${analysis.effectType === o.key ? CRIMSON : LIGHT_BORDER}`, background: analysis.effectType === o.key ? `${CRIMSON}08` : CARD_BG, transition: "all 0.2s" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: analysis.effectType === o.key ? CRIMSON : DARK }}>{o.label}</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{tx.effectWhy[o.key]}</div>
          </div>
        ))}
      </div>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: DARK, marginBottom: 10 }}>{tx.modelLabel}</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
        {[{ key: "random", label: tx.modelRandom, desc: tx.modelRandomDesc }, { key: "fixed", label: tx.modelFixed, desc: tx.modelFixedDesc }].map(m => (
          <div key={m.key} onClick={() => setA(p => ({ ...p, model: m.key }))} style={{ padding: "12px 16px", borderRadius: 12, cursor: "pointer", border: `1.5px solid ${analysis.model === m.key ? CRIMSON : LIGHT_BORDER}`, background: analysis.model === m.key ? `${CRIMSON}08` : CARD_BG, transition: "all 0.2s" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: analysis.model === m.key ? CRIMSON : DARK }}>{m.label}</div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{m.desc}</div>
          </div>
        ))}
      </div>
      <InputField label={tx.rationale} value={analysis.rationale || ""} onChange={v => setA(p => ({ ...p, rationale: v }))} placeholder={tx.rationalePh} multiline />
    </div>
  );
}

function Step2({ project, analysis, lang }) {
  const tx = T[lang]; const inc = getIncluded(project); const bin = isBinary(inc);
  const tsv = buildTSV(inc, bin); const csv = buildCSV(inc, bin);
  const headers = bin ? [tx.studyLabel, tx.eventsI, tx.totalI, tx.eventsC, tx.totalC] : [tx.studyLabel, tx.meanI, tx.sdI, tx.nI, tx.meanC, tx.sdC, tx.nC];

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: DARK, marginBottom: 8 }}>{tx.prepTitle}</h3>
      <p style={{ fontSize: 13, color: MUTED, marginBottom: 6 }}>{tx.prepDesc}</p>
      <ul style={{ fontSize: 13, color: MUTED, marginBottom: 20, paddingLeft: 20, lineHeight: 1.8 }}>
        <li>{tx.prepOption1}</li><li>{tx.prepOption2}</li><li>{tx.prepOption3}</li>
      </ul>
      <div style={{ overflowX: "auto", marginBottom: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr>{headers.map((h, i) => <th key={i} style={{ padding: "8px 10px", textAlign: i === 0 ? "left" : "center", color: DARK, fontWeight: 600, borderBottom: `2px solid ${LIGHT_BORDER}`, whiteSpace: "nowrap", fontSize: 12 }}>{h}</th>)}</tr></thead>
          <tbody>{inc.map((s, idx) => (
            <tr key={s.id} style={{ background: idx % 2 === 0 ? CARD_BG : LIGHT_BG }}>
              <td style={{ padding: "8px 10px", borderBottom: `1px solid ${LIGHT_BORDER}`, fontWeight: 500, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.citation || `Study ${idx + 1}`}</td>
              {bin
                ? <><td style={cellSt}>{s.tx?.events}</td><td style={cellSt}>{s.tx?.total}</td><td style={cellSt}>{s.ctrl?.events}</td><td style={cellSt}>{s.ctrl?.total}</td></>
                : <><td style={cellSt}>{s.txCont?.mean}</td><td style={cellSt}>{s.txCont?.sd}</td><td style={cellSt}>{s.txCont?.n}</td><td style={cellSt}>{s.ctrlCont?.mean}</td><td style={cellSt}>{s.ctrlCont?.sd}</td><td style={cellSt}>{s.ctrlCont?.n}</td></>}
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <CopyButton text={tsv} label={`📋 ${tx.copyTable}`} copiedLabel={tx.copied} />
        <Btn small onClick={() => downloadFile(csv, "ma_data.csv")} style={{ fontSize: 12 }}>📥 {tx.downloadCSV}</Btn>
      </div>
    </div>
  );
}

function Step3({ project, analysis, setA, lang, user }) {
  const tx = T[lang]; const [tab, setTab] = useState("webr");
  const inc = getIncluded(project); const bin = isBinary(inc);
  const modColumns = project.moderatorColumns || [];
  const rCode = buildRCode(inc, bin, analysis.effectType, analysis.model, modColumns);

  const tabs = [
    { key: "webr", icon: "🧪", label: tx.tabWebR },
    { key: "online", icon: "🌐", label: tx.tabOnline },
    { key: "r", icon: "📟", label: tx.tabR },
  ];

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: DARK, marginBottom: 8 }}>{tx.runTitle}</h3>
      <p style={{ fontSize: 13, color: MUTED, marginBottom: 16 }}>{tx.runDesc}</p>

      {/* Three tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderRadius: 10, overflow: "hidden", border: `1px solid ${LIGHT_BORDER}` }}>
        {tabs.map((t, i) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex: 1, padding: "10px 12px", fontSize: 12, fontWeight: 600, fontFamily: FONT,
            background: tab === t.key ? `${CRIMSON}10` : CARD_BG,
            color: tab === t.key ? CRIMSON : MUTED,
            border: "none",
            borderRight: i < tabs.length - 1 ? `1px solid ${LIGHT_BORDER}` : "none",
            cursor: "pointer",
            transition: "all 0.2s",
          }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab: WebR In-Browser Analysis */}
      {tab === "webr" && (
        <WebRRunner
          rCode={rCode}
          lang={lang}
          user={user}
          pico={project.pico}
          effectType={analysis.effectType}
          model={analysis.model}
          moderatorColumns={modColumns}
          studies={inc}
          onAdvComplete={({ type, moderator }) => {
            setA(p => {
              const existing = p.completedAdvanced || [];
              if (existing.some(a => a.type === type && a.moderator === moderator)) return p;
              return { ...p, completedAdvanced: [...existing, { type, moderator }] };
            });
          }}
        />
      )}

      {/* Tab: Online Calculator */}
      {tab === "online" && (
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 12 }}>{tx.onlineTitle}</h4>
          <ol style={{ fontSize: 13, color: MUTED, lineHeight: 2, paddingLeft: 20, marginBottom: 20 }}>
            {tx.onlineSteps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          <a href="https://smuonco.shinyapps.io/Onlinemeta/" target="_blank" rel="noopener noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 10, background: CRIMSON, color: "#FFF", fontSize: 14, fontWeight: 600, textDecoration: "none", fontFamily: FONT }}>
            {tx.onlineLink}
          </a>
          <div style={{ marginTop: 20, fontSize: 12, color: MUTED }}>
            {tx.onlineAlt}
            <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
              <a href="https://metaanalysisonline.com/" target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontSize: 12 }}>MetaAnalysisOnline.com</a>
              <a href="https://www.meta-mar.com/" target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontSize: 12 }}>Meta-Mar</a>
            </div>
          </div>
        </div>
      )}

      {/* Tab: R Code */}
      {tab === "r" && (
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: DARK, marginBottom: 12 }}>{tx.rTitle}</h4>
          <ol style={{ fontSize: 13, color: MUTED, lineHeight: 2, paddingLeft: 20, marginBottom: 12 }}>
            {tx.rSteps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          <Hint>{tx.rInstallNote}</Hint>
          <div style={{ position: "relative", marginBottom: 16 }}>
            <pre style={{ background: "#1E1E2E", color: "#CDD6F4", borderRadius: 12, padding: 20, fontSize: 14, lineHeight: 1.7, overflowX: "auto", fontFamily: "'Courier New', Courier, monospace", maxHeight: 480, tabSize: 2 }}>{rCode}</pre>
            <div style={{ position: "absolute", top: 10, right: 10 }}>
              <CopyButton text={rCode} label={tx.rCopyCode} copiedLabel={tx.copied} />
            </div>
          </div>
        </div>
      )}

      {/* ── Interpretation fields (type here while looking at results) ── */}
      <div style={{ marginTop: 32, paddingTop: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: LIGHT_BORDER }} />
          <button onClick={() => setA(p => ({ ...p, _showInterpret: !p._showInterpret }))}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 20px", borderRadius: 10,
              fontSize: 14, fontWeight: 700, fontFamily: FONT,
              cursor: "pointer",
              border: `2px solid ${BLUE}40`,
              background: analysis._showInterpret ? `${BLUE}10` : CARD_BG,
              color: BLUE,
              transition: "all 0.2s",
            }}>
            {tx.interpretNowTitle}
            <span style={{ fontSize: 11, transform: analysis._showInterpret ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>▼</span>
          </button>
          <div style={{ flex: 1, height: 1, background: LIGHT_BORDER }} />
        </div>
        {analysis._showInterpret && <>
        <p style={{ fontSize: 13, color: MUTED, marginBottom: 16, lineHeight: 1.6 }}>{tx.interpretNowDesc}</p>
        <InputField label={tx.forestQ1} value={analysis.forestQ1 || ""} onChange={v => setA(p => ({ ...p, forestQ1: v }))} placeholder={tx.forestQ1Ph} multiline />
        <InputField label={tx.forestQ2} value={analysis.forestQ2 || ""} onChange={v => setA(p => ({ ...p, forestQ2: v }))} placeholder={tx.forestQ2Ph} multiline />
        <InputField label={tx.hetInterpret} value={analysis.hetInterpretation || ""} onChange={v => setA(p => ({ ...p, hetInterpretation: v }))} placeholder={tx.hetInterpretPh} multiline />
        <InputField label={tx.funnelInterpret} value={analysis.funnelAssessment || ""} onChange={v => setA(p => ({ ...p, funnelAssessment: v }))} placeholder={tx.funnelPh} multiline />

        {/* Advanced interpretation fields — appear as analyses complete */}
        {(analysis.completedAdvanced || []).map((adv) => {
          const key = adv.moderator ? `${adv.type}_${adv.moderator}` : adv.type;
          const label = tx.advInterpretLabel?.[adv.type] || adv.type;
          const ph = tx.advInterpretPh?.[adv.type] || "";
          const fullLabel = adv.moderator ? `${label} (${adv.moderator})` : label;
          return (
            <InputField key={key} label={fullLabel}
              value={(analysis.advInterpretations || {})[key] || ""}
              onChange={v => setA(p => ({ ...p, advInterpretations: { ...(p.advInterpretations || {}), [key]: v } }))}
              placeholder={ph} multiline />
          );
        })}
        </>}
      </div>
    </div>
  );
}

function Step4({ analysis, setA, project, lang }) {
  const tx = T[lang]; const [aiLoading, setAiLoading] = useState(false);
  const [aiFeedback, setAiFeedback] = useState(analysis._interpretFeedback || null);
  const abortRef = useRef(null);
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  // At least 2 interpretation fields must have content (basic + advanced)
  const filledFields = [analysis.forestQ1, analysis.forestQ2, analysis.hetInterpretation, analysis.funnelAssessment].filter(v => (v || "").trim().length > 10);
  const advFields = Object.values(analysis.advInterpretations || {}).filter(v => (v || "").trim().length > 10);
  const canCheck = filledFields.length + advFields.length >= 2;

  const handleAiCheck = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setAiLoading(true); setAiFeedback(null);
    const isZh = lang === "zh";
    const picoStr = `P: ${project.pico?.p || "?"} | I: ${project.pico?.i || "?"} | C: ${project.pico?.c || "?"} | O: ${project.pico?.o || "?"}`;
    const studyCount = getIncluded(project).length;

    const systemPrompt = isZh
      ? `你是統合分析方法學專家與教學者。學生完成基本統合分析後正在練習解讀結果。
研究主題 — ${picoStr}
納入研究：${studyCount} 篇　效果量類型：${analysis.effectType}　模型：${analysis.model === "random" ? "隨機效果" : "固定效果"}

請針對學生的解讀回答，逐項檢查：
1. 整體效果量解讀是否正確？（方向、大小、顯著性的描述是否準確）
2. 權重和一致性的描述是否合理？
3. 異質性解讀是否正確？（I² 的分級、可能原因的推測）
4. 漏斗圖解讀是否恰當？（若有填寫）
5. 進階分析解讀是否正確？（若有填寫，如敏感性分析、發表偏差檢測等）
6. 是否遺漏了重要的面向？（例如只報告統計顯著性但忽略臨床意義）

每項以「✅」（正確）或「⚠️」（需改進）開頭，簡短說明。最後一句給整體建議。
繁體中文，6-8 句。不用 Markdown。`
      : `You are a meta-analysis methodology expert and educator. The student has completed a basic MA and is practicing result interpretation.
Study topic — ${picoStr}
Studies: ${studyCount} | Effect: ${analysis.effectType} | Model: ${analysis.model === "random" ? "Random-Effects" : "Fixed-Effect"}

Review the student's interpretation answers, checking each area:
1. Is the pooled effect interpretation correct? (direction, magnitude, significance described accurately)
2. Is the weight/consistency description reasonable?
3. Is the heterogeneity interpretation correct? (I² classification, possible sources)
4. Is the funnel plot interpretation appropriate? (if provided)
5. Are advanced analysis interpretations correct? (if provided, e.g., sensitivity analysis, publication bias tests)
6. Are any important aspects missing? (e.g., reporting only statistical significance but ignoring clinical meaning)

Start each item with "✅" (correct) or "⚠️" (needs improvement), with a brief explanation. End with one overall suggestion.
6-8 sentences. No Markdown.`;

    const userMsg = [
      `Q1 (Pooled effect): ${analysis.forestQ1 || "(empty)"}`,
      `Q2 (Consistency & weights): ${analysis.forestQ2 || "(empty)"}`,
      `Q3 (Heterogeneity): ${analysis.hetInterpretation || "(empty)"}`,
      `Q4 (Funnel plot): ${analysis.funnelAssessment || "(empty)"}`,
      ...(analysis.completedAdvanced || []).map(adv => {
        const key = adv.moderator ? `${adv.type}_${adv.moderator}` : adv.type;
        return `Advanced — ${adv.type}${adv.moderator ? ` (${adv.moderator})` : ""}: ${(analysis.advInterpretations || {})[key] || "(empty)"}`;
      }),
    ].join("\n");

    try {
      const resp = await fetch("/api/ai-feedback", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ system: systemPrompt, userMessage: userMsg }), signal: controller.signal });
      if (!resp.ok) {
        setAiFeedback(isZh ? "AI 服務暫時無法使用，請稍後再試。" : "AI service temporarily unavailable. Please try again later.");
        return;
      }
      const data = await resp.json();
      if (controller.signal.aborted) return;
      const text = data.content?.map(i => i.text || "").join("") || (isZh ? "無法取得回饋" : "Could not get feedback");
      // Only persist successful AI replies — never persist a connection-error string.
      setAiFeedback(text); setA(p => ({ ...p, _interpretFeedback: text }));
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Step4 AI feedback failed:", err);
      setAiFeedback(isZh ? "連線錯誤" : "Connection error");
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setAiLoading(false);
      }
    }
  };

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: DARK, marginBottom: 8 }}>{tx.reviewTitle}</h3>
      <p style={{ fontSize: 13, color: MUTED, marginBottom: 20, lineHeight: 1.6 }}>{tx.reviewDesc}</p>

      {/* Interpretation fields — editable for corrections */}
      <InputField label={tx.forestQ1} value={analysis.forestQ1 || ""} onChange={v => setA(p => ({ ...p, forestQ1: v }))} placeholder={tx.forestQ1Ph} multiline />
      <InputField label={tx.forestQ2} value={analysis.forestQ2 || ""} onChange={v => setA(p => ({ ...p, forestQ2: v }))} placeholder={tx.forestQ2Ph} multiline />
      <InputField label={tx.hetInterpret} value={analysis.hetInterpretation || ""} onChange={v => setA(p => ({ ...p, hetInterpretation: v }))} placeholder={tx.hetInterpretPh} multiline />
      <InputField label={tx.funnelInterpret} value={analysis.funnelAssessment || ""} onChange={v => setA(p => ({ ...p, funnelAssessment: v }))} placeholder={tx.funnelPh} multiline />

      {/* Advanced interpretation fields — match what was completed in Step 3 */}
      {(analysis.completedAdvanced || []).map((adv) => {
        const key = adv.moderator ? `${adv.type}_${adv.moderator}` : adv.type;
        const label = tx.advInterpretLabel?.[adv.type] || adv.type;
        const ph = tx.advInterpretPh?.[adv.type] || "";
        const fullLabel = adv.moderator ? `${label} (${adv.moderator})` : label;
        return (
          <InputField key={key} label={fullLabel}
            value={(analysis.advInterpretations || {})[key] || ""}
            onChange={v => setA(p => ({ ...p, advInterpretations: { ...(p.advInterpretations || {}), [key]: v } }))}
            placeholder={ph} multiline />
        );
      })}

      {/* AI Interpretation Check */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
        <Btn primary onClick={handleAiCheck} disabled={!canCheck || aiLoading}>{aiLoading ? tx.aiCheckingInterpret : `🤖 ${tx.aiCheckInterpret}`}</Btn>
        {!canCheck && <span style={{ fontSize: 12, color: MUTED }}>{lang === "zh" ? "請至少在上一步填寫 2 項解讀" : "Fill in at least 2 interpretation fields in the previous step"}</span>}
      </div>
      <AiFeedbackBox feedback={aiFeedback} loading={aiLoading} lang={lang} />
    </div>
  );
}

function Step5({ analysis, setA, project, lang }) {
  const tx = T[lang];
  const gradeOpts = [{ key: "high", label: tx.gradeHigh, color: GREEN }, { key: "moderate", label: tx.gradeMod, color: BLUE }, { key: "low", label: tx.gradeLow, color: AMBER }, { key: "very_low", label: tx.gradeVLow, color: RED }];

  return (
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: DARK, marginBottom: 16 }}>{tx.conclusionTitle}</h3>
      <InputField label={tx.mainFinding} value={analysis.mainFinding || ""} onChange={v => setA(p => ({ ...p, mainFinding: v }))} placeholder={tx.mainFindingPh} multiline />
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: DARK, marginBottom: 10 }}>{tx.certainty}</label>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {gradeOpts.map(g => (
          <button key={g.key} onClick={() => setA(p => ({ ...p, certainty: g.key }))} style={{ flex: 1, padding: "8px 0", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONT, cursor: "pointer", border: `1.5px solid ${analysis.certainty === g.key ? g.color : LIGHT_BORDER}`, background: analysis.certainty === g.key ? `${g.color}14` : CARD_BG, color: analysis.certainty === g.key ? g.color : MUTED }}>{g.label}</button>
        ))}
      </div>
      <InputField label={tx.certRationale} value={analysis.certRationale || ""} onChange={v => setA(p => ({ ...p, certRationale: v }))} placeholder={tx.certRationalePh} />
      <InputField label={tx.limitations} value={analysis.limitations || ""} onChange={v => setA(p => ({ ...p, limitations: v }))} placeholder={tx.limitationsPh} multiline />
      <InputField label={tx.implications} value={analysis.implications || ""} onChange={v => setA(p => ({ ...p, implications: v }))} placeholder={tx.implicationsPh} multiline />

      {/* ── Full Project Review (covers conclusion check too) ── */}
      <FullReviewSection analysis={analysis} setA={setA} project={project} lang={lang} />
    </div>
  );
}
function FullReviewSection({ analysis, setA, project, lang }) {
  const tx = T[lang]; const [aiLoading, setAiLoading] = useState(false);
  const [aiFeedback, setAiFeedback] = useState(analysis._fullReviewFeedback || null);
  const abortRef = useRef(null);
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const handleFullReview = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setAiLoading(true); setAiFeedback(null);
    const isZh = lang === "zh";
    const inc = getIncluded(project);

    // Build RoB summary
    const robSummary = inc.map(s => {
      const rob = s.rob || {};
      const domains = Object.entries(rob).map(([k, v]) => `${k}: ${v}`).join(", ");
      return `${s.citation}: ${domains || "not rated"}`;
    }).join("\n");

    const systemPrompt = isZh
      ? `你是統合分析方法學專家與教學者。請全面審查這位學生的完整統合分析專案，從 PICO 到結論。

針對以下 6 個面向逐一評估，每項以「✅」（良好）或「⚠️」（需改進）開頭：

1. 內部一致性：PICO → 搜尋策略 → 納入研究 → 效果量選擇 → 結論，是否前後連貫？
2. 方法學適當性：效果量類型和模型選擇是否合理？是否與資料型態匹配？
3. 偏差風險考量：RoB 評估結果是否反映在解讀和結論中？高偏差研究是否有適當處理？
4. 結果解讀品質：統計結果的解讀是否準確？是否區分統計顯著性和臨床意義？
5. 報告完整性：是否報告了所有必要的統計數據（效果量、CI、p 值、I²）？
6. 結論合理性：GRADE 評級是否與證據品質一致？臨床意義是否具體且有依據？

最後用 1-2 句給出整體評價和最重要的改進建議。
繁體中文，8-12 句。不用 Markdown。`
      : `You are a meta-analysis methodology expert and educator. Please conduct a comprehensive review of this student's complete meta-analysis project, from PICO through conclusions.

Evaluate these 6 dimensions, starting each with "✅" (good) or "⚠️" (needs improvement):

1. Internal consistency: Is there a coherent thread from PICO → search → included studies → effect size choice → conclusions?
2. Methodological appropriateness: Are the effect size type and model reasonable? Do they match the data type?
3. Risk of bias consideration: Are RoB ratings reflected in the interpretation and conclusions? Are high-bias studies handled appropriately?
4. Interpretation quality: Is the statistical output interpreted accurately? Does the student distinguish statistical significance from clinical importance?
5. Reporting completeness: Are all necessary statistics reported (effect size, CI, p-value, I²)?
6. Conclusion reasonableness: Is the GRADE rating consistent with evidence quality? Are clinical implications specific and evidence-based?

End with 1-2 sentences of overall assessment and the single most important improvement suggestion.
8-12 sentences. No Markdown.`;

    const userMsg = [
      `=== PICO ===`,
      `P: ${project.pico?.p || "?"}`,
      `I: ${project.pico?.i || "?"}`,
      `C: ${project.pico?.c || "?"}`,
      `O: ${project.pico?.o || "?"}`,
      ``,
      `=== STUDIES (${inc.length} included) ===`,
      inc.map(s => s.citation).join(", "),
      ``,
      `=== RISK OF BIAS ===`,
      robSummary,
      ``,
      `=== ANALYSIS CHOICES ===`,
      `Effect size: ${analysis.effectType}`,
      `Model: ${analysis.model}`,
      `Rationale: ${analysis.rationale || "(empty)"}`,
      ``,
      `=== INTERPRETATION ===`,
      `Pooled effect: ${analysis.forestQ1 || "(empty)"}`,
      `Consistency/weights: ${analysis.forestQ2 || "(empty)"}`,
      `Heterogeneity: ${analysis.hetInterpretation || "(empty)"}`,
      `Funnel plot: ${analysis.funnelAssessment || "(empty)"}`,
      ``,
      `=== CONCLUSIONS ===`,
      `Main finding: ${analysis.mainFinding || "(empty)"}`,
      `GRADE: ${analysis.certainty || "(empty)"}`,
      `GRADE rationale: ${analysis.certRationale || "(empty)"}`,
      `Limitations: ${analysis.limitations || "(empty)"}`,
      `Implications: ${analysis.implications || "(empty)"}`,
    ].join("\n");

    try {
      const resp = await fetch("/api/ai-feedback", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ system: systemPrompt, userMessage: userMsg }), signal: controller.signal });
      if (!resp.ok) {
        setAiFeedback(isZh ? "AI 服務暫時無法使用，請稍後再試。" : "AI service temporarily unavailable. Please try again later.");
        return;
      }
      const data = await resp.json();
      if (controller.signal.aborted) return;
      const text = data.content?.map(i => i.text || "").join("") || (isZh ? "無法取得回饋" : "Could not get feedback");
      // Only persist successful AI replies — never persist a connection-error string.
      setAiFeedback(text); setA(p => ({ ...p, _fullReviewFeedback: text }));
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("FullReview AI feedback failed:", err);
      setAiFeedback(isZh ? "連線錯誤" : "Connection error");
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setAiLoading(false);
      }
    }
  };

  return (
    <div style={{ marginTop: 28, paddingTop: 24, borderTop: `2px dashed ${LIGHT_BORDER}` }}>
      <h4 style={{ fontSize: 15, fontWeight: 700, color: DARK, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
        🔍 {tx.aiFullReview}
      </h4>
      <p style={{ fontSize: 12, color: MUTED, marginBottom: 16, lineHeight: 1.6 }}>{tx.aiFullReviewDesc}</p>
      <Btn primary onClick={handleFullReview} disabled={aiLoading}>
        {aiLoading ? tx.aiFullReviewing : `🤖 ${tx.aiFullReview}`}
      </Btn>
      <AiFeedbackBox feedback={aiFeedback} loading={aiLoading} lang={lang} />
    </div>
  );
}

function Completion({ analysis, project, lang, onNavigate, onBack }) {
  const tx = T[lang];
  const [hovProfile, setHovProfile] = useState(false);
  const [hovReport, setHovReport] = useState(false);
  const [dlState, setDlState] = useState("idle"); // idle | loading | done

  const handleDownload = async () => {
    setDlState("loading");
    try {
      await generateReport(project, analysis, lang);
      setDlState("done");
      setTimeout(() => setDlState("idle"), 2000);
    } catch (e) {
      console.error("Report generation failed:", e);
      setDlState("idle");
    }
  };
  return (
    <div style={{ textAlign: "center" }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: DARK, marginBottom: 8 }}>{tx.congrats}</h2>
      <p style={{ fontSize: 14, color: MUTED, marginBottom: 32, lineHeight: 1.6, maxWidth: 540, margin: "0 auto 32px" }}>{tx.congratsDesc}</p>
      <Card style={{ textAlign: "left", maxWidth: 600, margin: "0 auto" }}>
        <h4 style={{ fontSize: 15, fontWeight: 700, color: DARK, marginBottom: 16 }}>{tx.summaryTitle}</h4>
        <div style={{ fontSize: 13, color: MUTED, lineHeight: 2 }}>
          <div><strong style={{ color: DARK }}>PICO:</strong> {project.pico?.p} / {project.pico?.i} / {project.pico?.c} / {project.pico?.o}</div>
          <div><strong style={{ color: DARK }}>{lang === "zh" ? "納入研究" : "Studies"}:</strong> {getIncluded(project).length}</div>
          <div><strong style={{ color: DARK }}>{lang === "zh" ? "效果量" : "Effect"}:</strong> {analysis.effectType} ({analysis.model === "random" ? (lang === "zh" ? "隨機效果" : "Random") : (lang === "zh" ? "固定效果" : "Fixed")})</div>
          {analysis.forestQ1 && <div style={{ background: `${CRIMSON}06`, borderRadius: 8, padding: "8px 12px", margin: "8px 0", fontSize: 13, color: DARK, lineHeight: 1.7 }}>{analysis.forestQ1}</div>}
          {analysis.mainFinding && <div style={{ marginTop: 8 }}><strong style={{ color: DARK }}>{lang === "zh" ? "主要發現" : "Finding"}:</strong> {analysis.mainFinding}</div>}
          {analysis.certainty && <div><strong style={{ color: DARK }}>GRADE:</strong> {analysis.certainty}</div>}
          {analysis.implications && <div><strong style={{ color: DARK }}>{lang === "zh" ? "臨床意義" : "Implications"}:</strong> {analysis.implications}</div>}
        </div>
      </Card>
      <div style={{ marginTop: 24, display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
        <Btn onClick={onBack}>← {lang === "zh" ? "上一步" : "Back"}</Btn>
        <button
          onClick={handleDownload}
          disabled={dlState === "loading"}
          onMouseEnter={() => setHovReport(true)}
          onMouseLeave={() => setHovReport(false)}
          style={{
            padding: "10px 24px", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: FONT,
            cursor: dlState === "loading" ? "wait" : "pointer",
            border: `1.5px solid ${hovReport ? CRIMSON : LIGHT_BORDER}`,
            background: hovReport ? `${CRIMSON}0A` : CARD_BG,
            color: dlState === "done" ? GREEN : hovReport ? CRIMSON : DARK,
            transition: "all 0.2s",
          }}>
          {dlState === "loading" ? (lang === "zh" ? "⏳ 產生中..." : "⏳ Generating...") : dlState === "done" ? (lang === "zh" ? "✓ 已下載" : "✓ Downloaded") : (lang === "zh" ? "📄 下載報告" : "📄 Download Report")}
        </button>
        <button
          onClick={() => onNavigate("profile")}
          onMouseEnter={() => setHovProfile(true)}
          onMouseLeave={() => setHovProfile(false)}
          style={{
            padding: "10px 24px", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: FONT,
            cursor: "pointer", border: "none", color: "#FFF",
            background: hovProfile ? "#0A5F67" : "#11939E",
            boxShadow: hovProfile ? "0 4px 16px rgba(14,124,134,0.35)" : "0 4px 12px rgba(14,124,134,0.2)",
            transition: "all 0.2s",
          }}>
          🦕 {lang === "zh" ? "查看我的學習歷程" : "View My Learning Path"}
        </button>
      </div>
    </div>
  );
}

// ═══ MAIN ═══
export default function Final({ onNavigate, user, onLogin, onLogout }) {
  const { t, lang } = useI18n(); const tx = T[lang]; const [step, setStep] = useState(0);

  const [project] = useState(() => { try { const s = sessionStorage.getItem("ma_project_midterm"); if (s) return JSON.parse(s); } catch {} return null; });

  const [analysis, setA] = useState(() => {
    const inc = getIncluded(project); const bin = inc.length > 0 ? isBinary(inc) : true;
    const fresh = { effectType: bin ? "OR" : "MD", model: "random", rationale: "", forestQ1: "", forestQ2: "", hetInterpretation: "", funnelAssessment: "", mainFinding: "", certainty: "", certRationale: "", limitations: "", implications: "", completedAdvanced: [], advInterpretations: {}, _interpretFeedback: null, _fullReviewFeedback: null };
    try {
      const s = sessionStorage.getItem("ma_project_final");
      if (s) {
        const saved = JSON.parse(s);
        // Reconcile saved effectType against the current project's outcome type.
        // If the user changed the project (e.g. swapped from binary RCTs to
        // continuous outcomes in the midterm), the persisted "OR" is no longer
        // valid and would silently propagate to the R code template.
        const binaryEffects = ["OR", "RR", "RD"];
        const continuousEffects = ["MD", "SMD"];
        const allowed = bin ? binaryEffects : continuousEffects;
        if (saved.effectType && !allowed.includes(saved.effectType)) {
          saved.effectType = bin ? "OR" : "MD";
        }
        return { ...fresh, ...saved };
      }
    } catch {}
    return fresh;
  });

  useEffect(() => { try { sessionStorage.setItem("ma_project_final", JSON.stringify(analysis)); } catch {} }, [analysis]);

  if (!project || !getIncluded(project).length) {
    return (
      <div style={{ fontFamily: "'Noto Sans TC', 'Outfit', sans-serif", background: LIGHT_BG, minHeight: "100vh" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@300;400;500;600;700;900&family=Source+Serif+4:wght@400;600;700;900&family=Outfit:wght@300;400;500;600;700&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { background: ${LIGHT_BG}; }
          ::selection { background: ${CRIMSON}22; color: ${DARK}; }
        `}</style>
        <SiteNav onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} courseId="final" courseLabel={tx.title} courseColor={CRIMSON} />
        <div style={{ maxWidth: 500, margin: "80px auto", textAlign: "center", padding: "0 20px" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: DARK, marginBottom: 8 }}>{tx.noData}</h2>
          <Btn primary onClick={() => onNavigate("midterm")} style={{ marginTop: 16 }}>{tx.goToMidterm}</Btn>
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px dashed ${LIGHT_BORDER}` }}>
            <p style={{ fontSize: 13, color: MUTED, marginBottom: 12 }}>
              {lang === "zh" ? "或上傳先前下載的專案檔" : "Or upload a previously downloaded project file"}
            </p>
            <label style={{
              display: "inline-block", padding: "8px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
              fontFamily: FONT, cursor: "pointer",
              border: `1.5px solid ${LIGHT_BORDER}`, background: CARD_BG, color: MUTED,
            }}>
              📂 {lang === "zh" ? "上傳專案檔 (.json)" : "Upload Project File (.json)"}
              <input type="file" accept=".json" style={{ display: "none" }} onChange={e => {
                const file = e.target.files?.[0]; if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  try {
                    const data = JSON.parse(reader.result);
                    if (!data.pico || !data.studies?.length) { alert(lang === "zh" ? "檔案格式不正確" : "Invalid project file"); return; }
                    sessionStorage.setItem("ma_project_midterm", JSON.stringify(data));
                    window.location.reload();
                  } catch { alert(lang === "zh" ? "檔案讀取失敗" : "Failed to read file"); }
                };
                reader.readAsText(file);
              }} />
            </label>
          </div>
        </div>
      </div>
    );
  }

  const steps = [tx.step1, tx.step2, tx.step3, tx.step4, tx.step5];
  const isLast = step === steps.length - 1; const isDone = step === steps.length;

  return (
    <div style={{ fontFamily: "'Noto Sans TC', 'Outfit', sans-serif", background: LIGHT_BG, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+TC:wght@300;400;500;600;700;900&family=Source+Serif+4:wght@400;600;700;900&family=Outfit:wght@300;400;500;600;700&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: ${LIGHT_BG}; }
        ::selection { background: ${CRIMSON}22; color: ${DARK}; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
      <SiteNav onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} courseId="final" courseLabel={tx.title} courseColor={CRIMSON} />
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 20px 60px" }}>
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ width: 24, height: 2, background: CRIMSON, borderRadius: 1 }} />
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: CRIMSON }}>Phase 2</span>
          </div>
          <h1 style={{ fontFamily: "'Noto Sans TC', 'Source Serif 4', serif", fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 900, lineHeight: 1.15, color: DARK, marginBottom: 16 }}>{tx.title}</h1>
          <p style={{ fontSize: 14, color: MUTED, marginBottom: 8 }}>{tx.subtitle}</p>
          <div style={{ background: `${CRIMSON}06`, border: `1px solid ${CRIMSON}15`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: MUTED }}>
            <strong style={{ color: DARK }}>PICO:</strong> {project.pico?.p} | {project.pico?.i} | {project.pico?.c} | {project.pico?.o}
          </div>
        </div>

        {!isDone && <StepIndicator steps={steps} current={step} onStepClick={setStep} />}

        <Card style={{ marginBottom: 24 }}>
          {step === 0 && <Step1 analysis={analysis} setA={setA} project={project} lang={lang} />}
          {step === 1 && <Step2 project={project} analysis={analysis} lang={lang} />}
          {step === 2 && <Step3 project={project} analysis={analysis} setA={setA} lang={lang} user={user} />}
          {step === 3 && <Step4 analysis={analysis} setA={setA} project={project} lang={lang} />}
          {step === 4 && <Step5 analysis={analysis} setA={setA} project={project} lang={lang} />}
          {isDone && <Completion analysis={analysis} project={project} lang={lang} onNavigate={onNavigate} onBack={() => setStep(s => s - 1)} />}
        </Card>

        {!isDone ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Btn onClick={() => setStep(s => s - 1)} disabled={step === 0}>{tx.prev}</Btn>
            <div style={{ fontSize: 12, color: MUTED }}>{step + 1} / {steps.length}</div>
            <Btn primary onClick={() => setStep(s => s + 1)}>{isLast ? (lang === "zh" ? "完成 🎉" : "Finish 🎉") : tx.next}</Btn>
          </div>
        ) : null}

        <div style={{ textAlign: "center", marginTop: 32 }}>
          <button onClick={() => onNavigate("hub")} style={{ background: "none", border: "none", fontSize: 13, color: MUTED, cursor: "pointer", fontFamily: FONT }}>← {tx.backToHub}</button>
        </div>
      </div>
    </div>
  );
}
